import { c as _c } from "react/compiler-runtime";
import { getLogger } from '@onefootprint/logger';
import type { AgentSuggestionOp } from '@onefootprint/request-types/dashboard';
import { useBuilder } from './provider';
import type { BundleNode, Suggestion } from './types';
import { isDecisionNode, isRulesNode, isTaskNode } from './types';

// `byField` / `byArtifact` keys are `${nodeId}:${key}`. `byGap` keys by `parentNodeId`, or HEAD_GAP
// for AddNode at the top of the canvas. `byGroup` keys by `${rulesNodeId}:${groupId}`.
const {
  logWarn
} = getLogger({
  location: 'agent-builder-suggestions'
});
export const HEAD_GAP = 'head';
export type SuggestionBuckets = {
  byNode: Map<string, Suggestion[]>;
  byField: Map<string, Suggestion[]>;
  byArtifact: Map<string, Suggestion[]>;
  nodeMetaByNode: Map<string, Suggestion[]>;
  newFieldsByNode: Map<string, Suggestion[]>;
  newArtifactsByNode: Map<string, Suggestion[]>;
  byGap: Map<string, Suggestion[]>;
  rulesMetaByNode: Map<string, Suggestion[]>;
  byGroup: Map<string, Suggestion[]>;
  newGroupsByNode: Map<string, Suggestion[]>;
};
const emptyBuckets = (): SuggestionBuckets => ({
  byNode: new Map(),
  byField: new Map(),
  byArtifact: new Map(),
  nodeMetaByNode: new Map(),
  newFieldsByNode: new Map(),
  newArtifactsByNode: new Map(),
  byGap: new Map(),
  rulesMetaByNode: new Map(),
  byGroup: new Map(),
  newGroupsByNode: new Map()
});
const push = (map: Map<string, Suggestion[]>, key: string, s: Suggestion): void => {
  const list = map.get(key);
  if (list) {
    list.push(s);
    return;
  }
  map.set(key, [s]);
};
export const nodeAndKey = (nodeId: string, key: string): string => `${nodeId}:${key}`;
export const groupKey = (rulesNodeId: string, groupId: string): string => `${rulesNodeId}:${groupId}`;

// Drops suggestions whose target node id isn't in the current bundle.
export const selectSuggestionsByTarget = (suggestions: Suggestion[], nodes: BundleNode[]): SuggestionBuckets => {
  const knownTaskIds = new Set(nodes.filter(isTaskNode).map(n => n.id));
  const knownRulesIds = new Set(nodes.filter(isRulesNode).map(n => n.id));
  const knownDecisionIds = nodes.filter(isDecisionNode).map(n => n.id);
  const knownNodeIds = new Set([...knownTaskIds, ...knownRulesIds, ...knownDecisionIds]);
  const buckets = emptyBuckets();
  for (const s of suggestions) {
    bucketize(s, buckets, {
      knownTaskIds,
      knownRulesIds,
      knownNodeIds
    });
  }
  return buckets;
};

// Dispatch to the right bucket by op.kind. The trailing `assertNever(op)` keeps
// the if/else exhaustive at compile time, so a new BE op kind won't typecheck
// until it's handled here. At runtime it skips (doesn't throw) to survive a
// BE/FE deploy skew where the wire carries a kind this build doesn't know.
const bucketize = (s: Suggestion, buckets: SuggestionBuckets, {
  knownTaskIds,
  knownRulesIds,
  knownNodeIds
}: {
  knownTaskIds: Set<string>;
  knownRulesIds: Set<string>;
  knownNodeIds: Set<string>;
}): void => {
  const op = s.operation;
  if (op.kind === 'update_task_node_meta') {
    if (knownTaskIds.has(op.nodeId)) push(buckets.nodeMetaByNode, op.nodeId, s);
  } else if (op.kind === 'update_task_field' || op.kind === 'remove_task_field') {
    if (knownTaskIds.has(op.nodeId)) push(buckets.byField, nodeAndKey(op.nodeId, op.key), s);
  } else if (op.kind === 'add_task_field') {
    if (knownTaskIds.has(op.nodeId)) push(buckets.newFieldsByNode, op.nodeId, s);
  } else if (op.kind === 'update_task_artifact' || op.kind === 'remove_task_artifact') {
    if (knownTaskIds.has(op.nodeId)) push(buckets.byArtifact, nodeAndKey(op.nodeId, op.key), s);
  } else if (op.kind === 'add_task_artifact') {
    if (knownTaskIds.has(op.nodeId)) push(buckets.newArtifactsByNode, op.nodeId, s);
  } else if (op.kind === 'update_rules_node_meta') {
    if (knownRulesIds.has(op.nodeId)) push(buckets.rulesMetaByNode, op.nodeId, s);
  } else if (op.kind === 'update_rules_group' || op.kind === 'remove_rules_group') {
    if (knownRulesIds.has(op.nodeId)) push(buckets.byGroup, groupKey(op.nodeId, op.groupId), s);
  } else if (op.kind === 'add_rules_group') {
    if (knownRulesIds.has(op.nodeId)) push(buckets.newGroupsByNode, op.nodeId, s);
  } else if (op.kind === 'update_node') {
    if (knownNodeIds.has(op.node.id)) push(buckets.byNode, op.node.id, s);
  } else if (op.kind === 'remove_node') {
    if (knownNodeIds.has(op.nodeId)) push(buckets.byNode, op.nodeId, s);
  } else if (op.kind === 'add_node') {
    push(buckets.byGap, op.parentNodeId ?? HEAD_GAP, s);
  } else if (op.kind === 'update_norms' || op.kind === 'update_skills' || op.kind === 'update_inputs' || op.kind === 'update_outputs') {
    // Not node-targeted — surfaced by the norms/skills/input/output views via the selectors below.
  } else {
    assertNever(op);
  }
};
const assertNever = (op: never): void => {
  logWarn('Unhandled suggestion op kind, skipping', undefined, {
    kind: (op as {
      kind?: string;
    }).kind
  });
};
export const useSuggestionBuckets = () => {
  return useBuilder().suggestionBuckets;
};
const EMPTY: Suggestion[] = [];
export const useSuggestionsForTaskMeta = nodeId => {
  return useSuggestionBuckets().nodeMetaByNode.get(nodeId) ?? EMPTY;
};
export const useSuggestionsForField = (nodeId, key) => {
  return useSuggestionBuckets().byField.get(nodeAndKey(nodeId, key)) ?? EMPTY;
};
export const useSuggestionsForArtifact = (nodeId, key) => {
  return useSuggestionBuckets().byArtifact.get(nodeAndKey(nodeId, key)) ?? EMPTY;
};
export const useNewFieldSuggestions = nodeId => {
  return useSuggestionBuckets().newFieldsByNode.get(nodeId) ?? EMPTY;
};
export const useNewArtifactSuggestions = nodeId => {
  return useSuggestionBuckets().newArtifactsByNode.get(nodeId) ?? EMPTY;
};
export const useSuggestionsForNode = nodeId => {
  return useSuggestionBuckets().byNode.get(nodeId) ?? EMPTY;
};
export const useSuggestionsForRulesMeta = nodeId => {
  return useSuggestionBuckets().rulesMetaByNode.get(nodeId) ?? EMPTY;
};
export const useSuggestionsForGroup = (rulesNodeId, groupId) => {
  return useSuggestionBuckets().byGroup.get(groupKey(rulesNodeId, groupId)) ?? EMPTY;
};
export const useNewGroupSuggestions = rulesNodeId => {
  return useSuggestionBuckets().newGroupsByNode.get(rulesNodeId) ?? EMPTY;
};

// Whole-graph ops sit outside the canvas buckets; the norms/skills/input views read them directly.
const useWholeBundleSuggestions = kind => {
  const $ = _c(5);
  const t0 = useBuilder();
  let t1;
  if ($[0] !== kind || $[1] !== t0.state.bundle.suggestions) {
    let t2;
    if ($[3] !== kind) {
      t2 = s => s.operation.kind === kind;
      $[3] = kind;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = t0.state.bundle.suggestions.filter(t2);
    $[0] = kind;
    $[1] = t0.state.bundle.suggestions;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
export const useNormsSuggestions = () => {
  return useWholeBundleSuggestions("update_norms");
};
export const useSkillsSuggestions = () => {
  return useWholeBundleSuggestions("update_skills");
};
export const useInputsSuggestions = () => {
  return useWholeBundleSuggestions("update_inputs");
};
export const useOutputsSuggestions = () => {
  return useWholeBundleSuggestions("update_outputs");
};